<script setup>
import { computed } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
]);

const { uiSettings, updateUISettings } = useUISettings();

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const isOnKanbanLayout = computed(() => {
  const { LAYOUT_TYPES } = wootConstants;
  return (
    (uiSettings.value.conversation_display_type || LAYOUT_TYPES.CONDENSED) ===
    LAYOUT_TYPES.KANBAN
  );
});

const kanbanGroupBy = computed(
  () => uiSettings.value.kanban_group_by || 'status'
);

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};

const toggleKanbanLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const { conversation_display_type: current = LAYOUT_TYPES.CONDENSED } =
    uiSettings.value;

  if (current === LAYOUT_TYPES.KANBAN) {
    const fallback = uiSettings.value.previously_used_conversation_display_type;
    const listType =
      fallback && fallback !== LAYOUT_TYPES.KANBAN
        ? fallback
        : LAYOUT_TYPES.CONDENSED;
    updateUISettings({ conversation_display_type: listType });
  } else {
    updateUISettings({
      conversation_display_type: LAYOUT_TYPES.KANBAN,
      previously_used_conversation_display_type: current,
    });
  }
};

const setKanbanGroupBy = groupBy => {
  updateUISettings({ kanban_group_by: groupBy });
};
</script>

<template>
  <div>
    <!-- Main header row -->
    <div
      class="flex items-center justify-between gap-2 px-3 h-[3.25rem]"
      :class="{
        'border-b border-n-strong':
          hasAppliedFiltersOrActiveFolders && !isOnKanbanLayout,
      }"
    >
      <div class="flex items-center justify-center min-w-0">
        <h1
          class="text-base font-medium truncate text-n-slate-12"
          :title="pageTitle"
        >
          {{ pageTitle }}
        </h1>
        <span
          v-if="
            allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
          "
          class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
          :title="allCount"
        >
          {{ formattedAllCount }}
        </span>
        <span
          v-if="!hasAppliedFiltersOrActiveFolders"
          class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        >
          {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
        </span>
      </div>
      <div class="flex items-center gap-1">
        <template v-if="hasAppliedFilters && !hasActiveFolders">
          <div class="relative">
            <NextButton
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
              icon="i-lucide-save"
              slate
              xs
              faded
              @click="emit('addFolders')"
            />
            <div
              id="saveFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
            icon="i-lucide-circle-x"
            ruby
            faded
            xs
            @click="emit('resetFilters')"
          />
        </template>
        <template v-if="hasActiveFolders">
          <div class="relative">
            <NextButton
              id="toggleConversationFilterButton"
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
              icon="i-lucide-pen-line"
              slate
              xs
              faded
              @click="emit('filtersModal')"
            />
            <div
              id="conversationFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
            icon="i-lucide-trash-2"
            ruby
            xs
            faded
            @click="emit('deleteFolders')"
          />
        </template>
        <div v-else class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
            icon="i-lucide-list-filter"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <ConversationBasicFilter
          v-if="!hasAppliedFiltersOrActiveFolders && !isOnKanbanLayout"
          :is-on-expanded-layout="isOnExpandedLayout"
          @change-filter="onBasicFilterChange"
        />
        <SwitchLayout
          v-if="!isOnKanbanLayout"
          :is-on-expanded-layout="isOnExpandedLayout"
          @toggle="toggleConversationLayout"
        />
        <!-- Kanban toggle button -->
        <NextButton
          v-tooltip.left="
            isOnKanbanLayout
              ? $t('CONVERSATION.KANBAN.EXIT_KANBAN')
              : $t('CONVERSATION.KANBAN.ENTER_KANBAN')
          "
          :icon="isOnKanbanLayout ? 'i-lucide-layout-list' : 'i-lucide-kanban'"
          slate
          xs
          faded
          class="flex-shrink-0 md:inline-flex hidden"
          :class="{ 'text-n-brand !bg-n-brand/10': isOnKanbanLayout }"
          @click="toggleKanbanLayout"
        />
      </div>
    </div>

    <!-- Kanban group-by bar (shown only in kanban mode) -->
    <div
      v-if="isOnKanbanLayout"
      class="flex items-center gap-1 px-3 pb-2 pt-1 border-b border-n-strong"
    >
      <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
      <span class="text-xs text-n-slate-10 mr-1">Agrupar:</span>
      <button
        class="px-2 py-0.5 rounded text-xs font-medium transition-colors"
        :class="
          kanbanGroupBy === 'status'
            ? 'bg-n-brand text-white'
            : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'
        "
        @click="setKanbanGroupBy('status')"
      >
        {{ $t('CONVERSATION.KANBAN.GROUP_BY_STATUS') }}
      </button>
      <button
        class="px-2 py-0.5 rounded text-xs font-medium transition-colors"
        :class="
          kanbanGroupBy === 'agent'
            ? 'bg-n-brand text-white'
            : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'
        "
        @click="setKanbanGroupBy('agent')"
      >
        {{ $t('CONVERSATION.KANBAN.GROUP_BY_AGENT') }}
      </button>
      <button
        class="px-2 py-0.5 rounded text-xs font-medium transition-colors"
        :class="
          kanbanGroupBy === 'team'
            ? 'bg-n-brand text-white'
            : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'
        "
        @click="setKanbanGroupBy('team')"
      >
        {{ $t('CONVERSATION.KANBAN.GROUP_BY_TEAM') }}
      </button>
      <button
        class="px-2 py-0.5 rounded text-xs font-medium transition-colors"
        :class="
          kanbanGroupBy === 'label'
            ? 'bg-n-brand text-white'
            : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'
        "
        @click="setKanbanGroupBy('label')"
      >
        {{ $t('CONVERSATION.KANBAN.GROUP_BY_LABEL') }}
      </button>
    </div>
  </div>
</template>
